import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, CheckCircle, Lock, Eye, EyeOff, ArrowLeft, Check, X } from 'lucide-react';

const ResetPassword = ({ token, onBackToHome }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const API_URL = window.location.origin;

  // Password strength validation
  const passwordChecks = {
    minLength: newPassword.length >= 10,
    hasUppercase: /[A-Z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword),
  };

  const isPasswordStrong = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password strength
    if (!isPasswordStrong) {
      setError('Password does not meet the requirements');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0f0f10] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white dark:bg-[#1a1a1a] border-4 border-[#FFD12D] rounded-2xl shadow-2xl p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-[#252525] dark:text-white mb-2">
              Password Reset Successful
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your password has been reset successfully. You can now log in with your new password.
            </p>
            <Button
              onClick={() => onBackToHome(true)}
              className="w-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold py-6 transition-all duration-300"
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f0f10] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/wayo-logo.png" 
                alt="Wayo Logo" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#252525] dark:text-white">WAYO</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-semibold">The Vendor Platform</p>
            </div>
          </div>
        </div>

        {/* Reset Form Card */}
        <div className="bg-white dark:bg-[#1a1a1a] border-4 border-[#FFD12D] rounded-2xl shadow-2xl p-8">
          <div className="border-b-2 border-[#FFD12D] pb-4 mb-6">
            <h2 className="text-2xl font-bold text-[#252525] dark:text-white flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#FFD12D] flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#252525]" />
              </div>
              Reset Password
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Enter your new password below
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-[#252525] dark:text-white flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#FFD12D]" />
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 pr-10 placeholder:text-gray-400"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#FFD12D]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#252525] dark:text-white flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#FFD12D]" />
                Confirm New Password
              </Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="border-2 border-gray-300 dark:border-gray-600 dark:bg-[#252525] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 placeholder:text-gray-400"
                data-testid="confirm-password-input"
              />
            </div>

            {/* Password Strength Indicator */}
            {newPassword && (
              <div className="bg-gray-50 dark:bg-[#252525] p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                <p className="text-xs font-semibold text-[#252525] dark:text-white mb-2">Password Requirements:</p>
                <div className="grid grid-cols-2 gap-1">
                  <div className={`flex items-center gap-1 text-xs ${passwordChecks.minLength ? 'text-green-600' : 'text-gray-400'}`}>
                    {passwordChecks.minLength ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    Minimum 10 characters
                  </div>
                  <div className={`flex items-center gap-1 text-xs ${passwordChecks.hasUppercase ? 'text-green-600' : 'text-gray-400'}`}>
                    {passwordChecks.hasUppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    One uppercase letter
                  </div>
                  <div className={`flex items-center gap-1 text-xs ${passwordChecks.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
                    {passwordChecks.hasNumber ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    One number
                  </div>
                  <div className={`flex items-center gap-1 text-xs ${passwordChecks.hasSpecial ? 'text-green-600' : 'text-gray-400'}`}>
                    {passwordChecks.hasSpecial ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    One special character
                  </div>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-semibold py-6 transition-all duration-300 shadow-lg"
              data-testid="reset-password-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => onBackToHome(false)}
              className="text-[#FFD12D] hover:text-[#252525] dark:hover:text-white font-semibold transition-colors flex items-center gap-2 mx-auto"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
